import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, Check, Clock, FilePenLine, FileText, X } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/shared/lib/cn";
import { motionTokens, riseItem, springs, staggerContainer } from "@/shared/lib/motion";
import { AnimatedNumber } from "@/shared/ui/AnimatedNumber";
import type { DecisionDashboard } from "../../api/claimsApi";

type Stage = DecisionDashboard["pipeline"][number];
type Formatter = (value: number | null | undefined) => string;

const CURRENCY = "USD";

/** Étapes du circuit SafeCheck, dans l'ordre, avec leur pastille numérotée et leur icône. */
const STEPS: Array<{ key: string; number: string; icon: LucideIcon; tile: string }> = [
  {
    key: "submitted",
    number: "bg-sky-300 text-white dark:bg-sky-400/80",
    icon: FileText,
    tile: "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
  },
  {
    key: "processing",
    number: "bg-blue-500 text-white",
    icon: FilePenLine,
    tile: "bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  },
];

function invoicesLabel(count: number): string {
  return count > 1 ? "factures" : "facture";
}

function Count({ count }: { count: number }) {
  return (
    <p className="text-2xl font-bold leading-none tracking-tight text-foreground">
      <AnimatedNumber value={count} format={(v) => Math.round(v).toLocaleString("fr-FR")} />
      <span className="ml-1.5 text-base font-medium">{invoicesLabel(count)}</span>
    </p>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{children}</p>;
}

function StepCard({ stage, step, index, formatMoney }: { stage: Stage; step: (typeof STEPS)[number]; index: number; formatMoney: Formatter }) {
  const Icon = step.icon;

  return (
    <motion.li
      variants={riseItem}
      whileHover={{ y: -motionTokens.distance.xs }}
      transition={springs.snappy}
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-card p-4"
    >
      <p className="flex items-center gap-2.5 text-sm font-semibold text-foreground">
        <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold", step.number)}>{index + 1}</span>
        {stage.label}
      </p>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <Count count={stage.count} />
          <p className="mt-2 text-sm tabular-nums text-muted-foreground">
            {formatMoney(stage.amount)} {CURRENCY}
          </p>
        </div>
        <span className={cn("grid h-12 w-12 shrink-0 place-items-center rounded-xl", step.tile)} aria-hidden="true">
          <Icon size={24} strokeWidth={1.75} />
        </span>
      </div>
      {stage.overdue > 0 ? (
        <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-danger-soft px-2.5 py-1 text-xs font-medium text-danger">
          <Clock size={13} aria-hidden="true" /> {stage.overdue} en retard
        </p>
      ) : null}
    </motion.li>
  );
}

function OutcomeCard({ stage, tone, formatMoney }: { stage: Stage; tone: "good" | "bad"; formatMoney: Formatter }) {
  const good = tone === "good";

  return (
    <motion.div
      variants={riseItem}
      whileHover={{ y: -motionTokens.distance.xs }}
      transition={springs.snappy}
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-xl border p-4",
        good
          ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/25 dark:bg-emerald-500/10"
          : "border-rose-200 bg-rose-50/70 dark:border-rose-500/25 dark:bg-rose-500/10",
      )}
    >
      <p className={cn("flex items-center gap-2.5 text-sm font-semibold", good ? "text-foreground" : "text-rose-600 dark:text-rose-300")}>
        <span
          className={cn(
            "grid h-6 w-6 shrink-0 place-items-center rounded-full",
            good ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300" : "bg-rose-500 text-white",
          )}
          aria-hidden="true"
        >
          {good ? <Check size={14} strokeWidth={3} /> : <X size={14} strokeWidth={3} />}
        </span>
        {stage.label}
      </p>
      <div>
        <Count count={stage.count} />
        <p className="mt-2 text-sm tabular-nums text-muted-foreground">
          {formatMoney(stage.amount)} {CURRENCY}
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Circuit SafeCheck : factures soumises, en cours de traitement, puis issue (validées, rejetées).
 */
export function InsurerPipeline({
  pipeline,
  formatMoney,
}: {
  pipeline: DecisionDashboard["pipeline"];
  formatMoney: Formatter;
}) {
  const byKey = new Map(pipeline.map((stage) => [stage.key, stage]));
  const steps = STEPS.map((step) => ({ step, stage: byKey.get(step.key) })).filter(
    (row): row is { step: (typeof STEPS)[number]; stage: Stage } => row.stage !== undefined,
  );
  const validated = byKey.get("validated");
  const rejected = byKey.get("rejected");

  return (
    <section className="theme-card overflow-hidden">
      <div className="flex flex-col gap-6 p-5 sm:p-6 xl:flex-row">
        <div className="min-w-0 xl:flex-[13]">
          <SectionLabel>Circuit SafeCheck</SectionLabel>
          <motion.ol
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-stretch"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {steps.map((row, index) => (
              <Fragment key={row.step.key}>
                <StepCard stage={row.stage} step={row.step} index={index} formatMoney={formatMoney} />
                {index < steps.length - 1 ? (
                  <motion.li variants={riseItem} className="hidden place-items-center text-muted-foreground md:grid" aria-hidden="true">
                    <ArrowRight size={16} />
                  </motion.li>
                ) : null}
              </Fragment>
            ))}
          </motion.ol>
        </div>

        <div className="hidden w-px self-stretch bg-border xl:block" aria-hidden="true" />

        <div className="min-w-0 xl:flex-[7]">
          <SectionLabel>Issue</SectionLabel>
          <motion.div className="grid gap-3 sm:grid-cols-2" variants={staggerContainer} initial="hidden" animate="visible">
            {validated ? <OutcomeCard stage={validated} tone="good" formatMoney={formatMoney} /> : null}
            {rejected ? <OutcomeCard stage={rejected} tone="bad" formatMoney={formatMoney} /> : null}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
